import { bookableOptionPricePerCalculator } from "@@/shared/acquire/resource-reference-options/bookable-option-price-calculator";
import type { BookableOptionValue } from "@@/shared/acquire/resource-reference-options/bookable-option-value";
import { isQuantityPickerBookableOptionValue } from "@@/shared/acquire/resource-reference-options/quantity-picker-bookable-option-value";
import { isTimeRangeOverDST } from "@@/shared/date-time/time-range";
import { inMinutes } from "@@/shared/date-time/time-spans";
import type { OrderItem } from "@@/shared/orders/order-items/order-item";
import type {
    OrderItem_V1,
    OrderItemBookable_V1,
} from "@@/shared/orders/order-items/order-item-v1";
import type {
    OrderItem_V2,
    OrderItemBookable_V2,
} from "@@/shared/orders/order-items/order-item-v2";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { repeat } from "@@/utilities/repeat";
import { sortBy } from "@@/utilities/sort";
import type { Discount } from "../discount";
import { percentageDiscountCalculatorOrderItem } from "./percentage/percentage-discount-calculator";
import { timePriceStepDiscountCalculator } from "./time-price-step/time-price-step-discount-calculator";
import type {
    DiscountPrice} from "./time-step/time-step-discount-calculator";
import {
    timeStepDiscountCalculator,
} from "./time-step/time-step-discount-calculator";
import { valueDiscountCalculatorOrderItem } from "./value/value-discount-calculator";

const calculateDiscountedPrice =
    (discounts: Discount[]) => (orderItem: OrderItem) => {
        const prices = discounts.map(d => {
            return calculateDiscount(d)(orderItem);
        });
        const bestPrice = prices[0];
        return bestPrice;
    };

const calculateDiscount = (discount: Discount) => (orderItem: OrderItem) => {
    switch (discount.type) {
        case "VALUE_DISCOUNT":
            return valueDiscountCalculatorOrderItem(discount)(orderItem);
        case "PERCENTAGE_DISCOUNT":
            return percentageDiscountCalculatorOrderItem(discount)(orderItem);
        default:
            break;
    }

    return orderItem.price;
};

const calculateDiscountedPriceOnOptionValue =
    (discounts: Discount[]) =>
    (
        orderItem: OrderItemBookable_V1 | OrderItemBookable_V2,
        value: BookableOptionValue,
    ) => {
        const numberOfBookedTime = inMinutes({
            seconds: orderItem.time.duration,
        });
        const defaultPricePer = bookableOptionPricePerCalculator({
            bookedMinutes: numberOfBookedTime,

            option: value,
            isSpanningDST: isTimeRangeOverDST(orderItem.time),
        });

        const quantit = isQuantityPickerBookableOptionValue(value)
            ? value.quantity.value
            : 0;

        const prices = discounts
            .map(d => {
                return calculateDiscountOnOptionValue(d)(orderItem, value);
            })
            .filter(Boolean);

        if (prices.length < 1) {
            return {
                pricePer: defaultPricePer,
                totalPrice: sumPrices(repeat(defaultPricePer, quantit)),
            } as DiscountPrice;
        }

        const bestPrice = prices
            .filter((p): p is DiscountPrice => !!p)
            .sort(sortBy(p => p.pricePer.amountIncludingVat))[0];
        return bestPrice;
    };

const calculateDiscountOnOptionValue =
    (discount: Discount) =>
    (orderItem: OrderItem_V1 | OrderItem_V2, value: BookableOptionValue) => {
        switch (discount.type) {
            case "TIME_STEP_DISCOUNT":
                return timeStepDiscountCalculator(discount)(orderItem, value);
            case "TIME_PRICE_STEP_DISCOUNT":
                return timePriceStepDiscountCalculator(discount)(
                    orderItem,
                    value,
                );

            default:
                break;
        }
        return undefined;
        //return orderItem.price;
    };

export {
    calculateDiscountedPrice,
    calculateDiscountedPriceOnOptionValue,
    calculateDiscountOnOptionValue,
};
